/**
 * <StudentShopPage> — « Boutique de gestes » de l'élève (page).
 *
 * L'écran d'aujourd'hui, rhabillé : ce que l'élève a (« Tes crédits », ses pétales derrière le
 * pétale plein du jeu), la phrase de Tina, qui parle en bulle avec sa tête (c'est sa quête que les
 * gestes font avancer), et les gestes (un arbre planté, un repas offert : image, nom, phrase,
 * « Acheté n fois », prix). La phrase est
 * remontée du pied de la liste sous les crédits : elle dit quoi faire de ce qu'on a, avant la liste.
 *
 * Jamais de dépense en un toucher : « Acheter » sur une carte ouvre la fiche du geste
 * (`<ShopGestureSheet>`), où l'achat se confirme. Le haut de la carte l'ouvre aussi, même sans
 * assez de pétales : la fiche se lit toujours. Un achat qui ne passe pas referme la fiche et se
 * dit en `<Snackbar>`, comme aujourd'hui.
 *
 * On y arrive depuis « Mon compte » (la ligne « Gestes »).
 *
 * Au large : ce que l'élève a et la phrase à gauche, les gestes à droite.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { columnStyles } from "../../atoms/containers/columns"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { EmptyState } from "../../molecules/EmptyState"
import { MessageRow } from "../../molecules/MessageRow"
import { Snackbar, type SnackbarProps } from "../../molecules/Snackbar"
import { StudentCounters } from "../../molecules/StudentCounters"
import { ShopGestureCard, type UIShopGesture } from "../../organisms/ShopGestureCard"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { ShopGestureSheet, type ShopGestureSheetProps } from "../../templates/ShopGestureSheet"
import { SHOP_TEXTS } from "./shopTexts"
import { StudentShopSkeleton } from "./StudentShopSkeleton"
import { type UIShopItem } from "../../../ports/primary/ui/types"

export type { UIShopItem }

export type StudentShopNotice = Pick<SnackbarProps, "message" | "tone">

/** La fiche ouverte, telle que l'écran la pilote : les pétales viennent de la page. */
export type StudentShopSheetProps = Omit<ShopGestureSheetProps, "petals">

export type StudentShopPageProps = {
    /** Les pétales de l'élève. `null` tant que la boutique n'est pas là. */
    petals: number | null
    /** Les gestes de la boutique. `null` tant qu'ils ne sont pas là. */
    items: UIShopItem[] | null
    /** Ce que dit celle qui porte la quête des gestes, en pied. Absent : rien. */
    hint?: string | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** La croix de l'en-tête. */
    onClose: () => void
    /** Une relecture est en cours : la flèche de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    /** Ouvre la fiche d'un geste : « Acheter » et le haut de sa carte. */
    onOpenGesture?: (gesture: UIShopGesture) => void
    /** La fiche du geste ouvert, et l'accusé de son achat. */
    sheet?: StudentShopSheetProps | null
    /** Un achat n'est pas passé. */
    notice?: StudentShopNotice | null
    onDismissNotice?: () => void
    testID?: string
}

const noop = () => {}

export function StudentShopPage(props: StudentShopPageProps) {
    const {
        petals,
        items,
        hint,
        loading = false,
        error,
        onRetry,
        onClose,
        refreshing = false,
        onRefresh,
        onOpenGesture,
        sheet,
        notice,
        onDismissNotice,
        testID,
    } = props

    const header = (
        <ClosableHeader title={SHOP_TEXTS.title} onClose={onClose} refreshing={refreshing} onRefresh={onRefresh} />
    )

    const hasHint = hint != null && hint !== ""
    const ready = petals != null && items != null

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<StudentShopSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {ready && (
                <View style={[columnStyles.columns, styles.fullWidth]}>
                    <Stack gap="4" style={columnStyles.column}>
                        <ShopBalance petals={petals} />
                        {hasHint && <Hint text={hint} />}
                    </Stack>

                    <Stack gap="4" style={columnStyles.column}>
                        {items.length === 0 && <EmptyState iconName="gift" title={SHOP_TEXTS.empty} />}
                        {items.map(({ gesture, purchasedCount }) => {
                            const open = onOpenGesture != null ? () => onOpenGesture(gesture) : undefined
                            return (
                                <ShopGestureCard
                                    key={gesture.id}
                                    gesture={gesture}
                                    purchasedCount={purchasedCount}
                                    canAfford={petals >= gesture.price}
                                    onBuy={open}
                                    onOpen={open}
                                />
                            )
                        })}
                    </Stack>
                </View>
            )}

            {sheet != null && petals != null && <ShopGestureSheet {...sheet} petals={petals} />}
            <Snackbar
                visible={notice != null}
                message={notice?.message ?? ""}
                tone={notice?.tone ?? "danger"}
                onDismiss={onDismissNotice ?? noop}
            />
        </PageTemplate>
    )
}

/** Ce que l'élève a : « Tes crédits », et ses pétales derrière le pétale plein du jeu. */
function ShopBalance(props: { petals: number }) {
    return (
        <Card p="4">
            <Row gap="3" justify="between">
                <Text size="text-lg" weight="bold">
                    {SHOP_TEXTS.balance}
                </Text>
                <StudentCounters petals={props.petals} />
            </Row>
        </Card>
    )
}

/** Celle qui porte la quête des gestes parle en bulle : la ligne pose sa tête, à sa taille du livre. */
function Hint(props: { text: string }) {
    return (
        <MessageRow
            message={{ id: "shop-hint", author: "other", text: props.text }}
            speaker="agent"
            tail
            champion="tina"
        />
    )
}

const styles = StyleSheet.create(() => ({
    /** La largeur de la colonne appartient à la page (`PageTemplate maxContentWidth`). */
    fullWidth: { width: "100%" },
}))
